import { supabase } from './supabase';

export interface SICoachRow {
  coach_id: string;
  user_id: string;
  full_name: string;
  email: string;
  tier: string;
  is_active: boolean;
  joined_at: string;
  client_count: number;
  session_count: number;
  analyses_completed: number;
  last_session_date: string | null;
}

export interface SIAdminStats {
  total_coaches: number;
  active_coaches: number;
  never_used: number;
  total_sessions: number;
  total_analyses: number;
}

export interface SIAdminClientRow {
  id: string;
  full_name: string;
  email: string;
  status: string;
  created_at: string;
  session_count: number;
  analyses_completed: number;
  last_session_date: string | null;
}

export interface SIAdminSessionRow {
  id: string;
  client_id: string;
  client_name: string;
  session_date: string;
  duration_minutes: number;
  session_type: string;
  analysis_status: string;
  created_at: string;
}

export async function getSIAdminCoaches(): Promise<SICoachRow[]> {
  const { data: coaches, error } = await supabase
    .from('coaches')
    .select(`
      id,
      user_id,
      tier,
      is_active,
      created_at,
      users!coaches_user_id_fkey (
        full_name,
        email
      ),
      coaching_clients (
        id,
        coaching_sessions (
          id,
          session_date,
          analysis_status
        )
      )
    `)
    .order('created_at', { ascending: false });

  if (error) throw error;

  return (coaches || []).map((c: any) => {
    const clients: any[] = c.coaching_clients || [];
    const allSessions = clients.flatMap((cl: any) => cl.coaching_sessions || []);
    const analyses = allSessions.filter((s: any) => s.analysis_status === 'complete');
    const sessionDates = allSessions
      .map((s: any) => s.session_date)
      .filter(Boolean)
      .sort()
      .reverse();

    return {
      coach_id: c.id,
      user_id: c.user_id,
      full_name: c.users?.full_name ?? 'Unknown',
      email: c.users?.email ?? '',
      tier: c.tier,
      is_active: c.is_active,
      joined_at: c.created_at,
      client_count: clients.length,
      session_count: allSessions.length,
      analyses_completed: analyses.length,
      last_session_date: sessionDates[0] ?? null,
    };
  });
}

export async function getSIAdminStats(coaches: SICoachRow[]): Promise<SIAdminStats> {
  return {
    total_coaches: coaches.length,
    active_coaches: coaches.filter(c => c.session_count > 0).length,
    never_used: coaches.filter(c => c.session_count === 0).length,
    total_sessions: coaches.reduce((sum, c) => sum + c.session_count, 0),
    total_analyses: coaches.reduce((sum, c) => sum + c.analyses_completed, 0),
  };
}

export async function getSIAdminCoachDetail(coachId: string): Promise<{
  coach: SICoachRow;
  clients: SIAdminClientRow[];
  sessions: SIAdminSessionRow[];
} | null> {
  const { data: coach, error: coachErr } = await supabase
    .from('coaches')
    .select(`
      id,
      user_id,
      tier,
      is_active,
      created_at,
      users!coaches_user_id_fkey (
        full_name,
        email
      ),
      coaching_clients (
        id,
        full_name,
        email,
        status,
        created_at,
        coaching_sessions (
          id,
          session_date,
          duration_minutes,
          session_type,
          analysis_status,
          created_at
        )
      )
    `)
    .eq('id', coachId)
    .maybeSingle();

  if (coachErr || !coach) return null;

  const clients: any[] = (coach as any).coaching_clients || [];
  const allSessions: SIAdminSessionRow[] = [];

  const clientRows: SIAdminClientRow[] = clients.map((cl: any) => {
    const sessions: any[] = cl.coaching_sessions || [];
    const analyses = sessions.filter((s: any) => s.analysis_status === 'complete');
    const sessionDates = sessions.map((s: any) => s.session_date).filter(Boolean).sort().reverse();

    for (const s of sessions) {
      allSessions.push({
        id: s.id,
        client_id: cl.id,
        client_name: cl.full_name,
        session_date: s.session_date,
        duration_minutes: s.duration_minutes,
        session_type: s.session_type,
        analysis_status: s.analysis_status,
        created_at: s.created_at,
      });
    }

    return {
      id: cl.id,
      full_name: cl.full_name,
      email: cl.email,
      status: cl.status,
      created_at: cl.created_at,
      session_count: sessions.length,
      analyses_completed: analyses.length,
      last_session_date: sessionDates[0] ?? null,
    };
  });

  allSessions.sort((a, b) => b.session_date.localeCompare(a.session_date));

  const c = coach as any;
  const allCoachSessions = clients.flatMap((cl: any) => cl.coaching_sessions || []);
  const sessionDates = allCoachSessions.map((s: any) => s.session_date).filter(Boolean).sort().reverse();

  const coachRow: SICoachRow = {
    coach_id: c.id,
    user_id: c.user_id,
    full_name: c.users?.full_name ?? 'Unknown',
    email: c.users?.email ?? '',
    tier: c.tier,
    is_active: c.is_active,
    joined_at: c.created_at,
    client_count: clients.length,
    session_count: allCoachSessions.length,
    analyses_completed: allCoachSessions.filter((s: any) => s.analysis_status === 'complete').length,
    last_session_date: sessionDates[0] ?? null,
  };

  return { coach: coachRow, clients: clientRows, sessions: allSessions };
}
